if (!customElements.get('l20-four-bikes')) {
  class L20FourBikes extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-l20-four-stage]');
      this.media = this.querySelector('[data-l20-four-media]');
      this.anchorLayer = this.querySelector('[data-l20-four-anchors]');
      this.panelsContainer = this.querySelector('.l20-four-bikes__panels');
      this.anchors = Array.from(this.querySelectorAll('[data-l20-four-anchor]'));
      this.panels = Array.from(this.querySelectorAll('[data-l20-four-panel]'));
      this.mobilePrevButton = this.querySelector('[data-l20-four-mobile-prev]');
      this.mobileNextButton = this.querySelector('[data-l20-four-mobile-next]');
      this.videos = Array.from(this.querySelectorAll('.l20-four-bikes__video'));
      this.scrollDistance = Number.parseInt(this.dataset.scrollDistance, 10) || 3600;
      this.requestedVideoDuration = Number.parseFloat(this.dataset.videoDuration) || 4;
      this.videoPhaseEnd = 0.52;
      this.activeIndex = Math.max(this.panels.length - 1, 0);
      this.anchorPlaybackOrder = [];
      this.videoSeekFrame = 0;
      this.pendingVideoSeek = null;
      this.panelRevealFrame = 0;
      this.panelRevealToken = 0;
      this.boundAnchorClick = this.handleAnchorClick.bind(this);
      this.boundAnchorKeydown = this.handleAnchorKeydown.bind(this);
      this.boundMobilePrevClick = () => this.moveMobileCarousel(-1);
      this.boundMobileNextClick = () => this.moveMobileCarousel(1);
      this.boundMobileScroll = this.handleMobileScroll.bind(this);
      this.classList.add('is-js');

      this.anchors.forEach((anchor) => {
        anchor.addEventListener('click', this.boundAnchorClick);
        anchor.addEventListener('keydown', this.boundAnchorKeydown);
      });
      this.mobilePrevButton?.addEventListener('click', this.boundMobilePrevClick);
      this.mobileNextButton?.addEventListener('click', this.boundMobileNextClick);
      const isDesktopViewport = window.matchMedia('(min-width: 769px)').matches;
      const initialVideo = this.getActiveVideo(isDesktopViewport);
      this.activateVideo(initialVideo);

      if (!this.stage || !this.media || this.panels.length === 0) return;
      if (!isDesktopViewport) this.enableMobileCarousel(initialVideo);
      this.waitForAnimationLibraries();
    }

    disconnectedCallback() {
      window.clearTimeout(this.libraryTimer);
      this.mediaContext?.revert();
      this.scrollTween?.kill();
      this.mobileCarouselTween?.kill();
      this.anchors?.forEach((anchor) => {
        anchor.removeEventListener('click', this.boundAnchorClick);
        anchor.removeEventListener('keydown', this.boundAnchorKeydown);
      });
      this.mobilePrevButton?.removeEventListener('click', this.boundMobilePrevClick);
      this.mobileNextButton?.removeEventListener('click', this.boundMobileNextClick);
      this.panelsContainer?.removeEventListener('scroll', this.boundMobileScroll);
      window.cancelAnimationFrame(this.mobileScrollFrame);
      window.cancelAnimationFrame(this.mobileAnimationFrame);
      window.cancelAnimationFrame(this.videoSeekFrame);
      window.cancelAnimationFrame(this.panelRevealFrame);
      this.videoSeekFrame = 0;
      this.panelRevealFrame = 0;
      this.pendingVideoSeek = null;
      if (this.panelsContainer) {
        this.panelsContainer.style.scrollSnapType = '';
        this.panelsContainer.style.willChange = '';
      }
      this.videos?.forEach((video) => video.pause());
    }

    waitForAnimationLibraries(attempt = 0) {
      if (window.gsap && window.ScrollTrigger) {
        this.initializeResponsiveAnimation();
        return;
      }

      if (attempt >= 100) {
        const isDesktop = window.matchMedia('(min-width: 769px)').matches;
        const activeVideo = this.getActiveVideo(isDesktop);
        if (isDesktop) {
          this.enableFallback(activeVideo);
        } else {
          this.enableMobileCarousel(activeVideo);
        }
        return;
      }

      this.libraryTimer = window.setTimeout(() => this.waitForAnimationLibraries(attempt + 1), 50);
    }

    initializeResponsiveAnimation() {
      window.gsap.registerPlugin(window.ScrollTrigger);
      this.mediaContext = window.gsap.matchMedia();
      this.mediaContext.add(
        {
          isDesktop: '(min-width: 769px)',
          reduceMotion: '(prefers-reduced-motion: reduce)',
        },
        (context) => {
          const { isDesktop, reduceMotion } = context.conditions;
          const activeVideo = this.getActiveVideo(isDesktop);
          this.pauseInactiveVideos(activeVideo);

          if (!isDesktop) {
            this.enableMobileCarousel(activeVideo);
            return () => this.disableMobileCarousel(activeVideo);
          }

          if (reduceMotion || !activeVideo) {
            this.enableFallback(activeVideo);
            return undefined;
          }

          return this.preparePinnedSequence(activeVideo, isDesktop);
        },
        this,
      );
    }

    getActiveVideo(isDesktop) {
      const desktopVideo = this.querySelector('.l20-four-bikes__video--desktop');
      const mobileVideo = this.querySelector('.l20-four-bikes__video--mobile');
      return isDesktop ? desktopVideo || mobileVideo : mobileVideo || desktopVideo;
    }

    activateVideo(activeVideo) {
      this.videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;

        if (video !== activeVideo) {
          video.pause();
          video.preload = 'none';
          return;
        }

        const shouldAutoplay = window.matchMedia('(max-width: 768.98px)').matches;
        video.preload = 'auto';
        const revealVideo = () => video.classList.add('is-loaded');
        if (video.readyState >= 2) {
          revealVideo();
        } else {
          video.addEventListener('loadeddata', revealVideo, { once: true });
          video.addEventListener('canplay', revealVideo, { once: true });
          video.load();
        }
        if (shouldAutoplay) this.primeVideo(video, false);
      });
    }

    pauseInactiveVideos(activeVideo) {
      this.activateVideo(activeVideo);
      this.videos.forEach((video) => {
        video.pause();
        video.controls = false;
        if (video !== activeVideo) this.seekVideo(video, 0);
      });
    }

    preparePinnedSequence(video, isDesktop) {
      let cancelled = false;
      let metadataTimer = 0;
      let removeMetadataListener = () => {};
      let removeErrorListener = () => {};

      const initialize = () => {
        if (cancelled || !Number.isFinite(video.duration) || video.duration <= 0) return;

        const seekEnd = Math.max(Math.min(video.duration - 0.04, this.requestedVideoDuration), 0);
        const state = { progress: 0 };
        const startX = isDesktop ? -78 : -48;
        const restingX = isDesktop ? 0 : 50;

        video.controls = false;
        video.pause();
        this.seekVideo(video, 0);
        this.primeVideo(video);
        this.classList.add('is-scroll-ready');
        this.classList.remove('is-anchor-content-visible');
        this.isAnchorLayerVisible = false;
        this.activeIndex = -1;
        this.anchorPlaybackOrder = this.getAnchorPlaybackOrder();
        this.cancelPendingPanelReveal();

        window.gsap.set(this.media, {
          xPercent: restingX + startX,
          filter: 'saturate(88%) brightness(96%)',
        });
        window.gsap.set(this.anchorLayer, { autoAlpha: 0 });
        window.gsap.set(this.panels, { autoAlpha: 0, x: 0 });

        const anchorSnapPoints = this.getAnchorSnapPoints();

        this.scrollTween = window.gsap.to(state, {
          progress: 1,
          duration: 1,
          ease: 'none',
          onUpdate: () => this.renderProgress(state.progress, video, seekEnd, startX, restingX),
          scrollTrigger: {
            id: `l20-four-bikes-${this.id}`,
            trigger: this.stage,
            start: 'center center',
            end: () => `+=${this.scrollDistance}`,
            pin: this.stage,
            pinSpacing: true,
            scrub: 0.2,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            snap: {
              snapTo: (value) => {
                if (value < this.videoPhaseEnd) return value;
                return anchorSnapPoints.reduce((nearest, point) =>
                  Math.abs(point - value) < Math.abs(nearest - value) ? point : nearest,
                );
              },
              duration: { min: 0.22, max: 0.5 },
              delay: 0.12,
              ease: 'power2.inOut',
            },
          },
        });

        this.renderProgress(0, video, seekEnd, startX, restingX);

        if (window.scheduleScrollTriggerRefresh) {
          window.scheduleScrollTriggerRefresh();
        } else {
          window.ScrollTrigger.refresh();
        }
      };

      if (video.readyState >= 1) {
        initialize();
      } else {
        const onMetadata = () => {
          window.clearTimeout(metadataTimer);
          initialize();
        };
        const onError = () => {
          window.clearTimeout(metadataTimer);
          this.enableFallback(video);
        };
        video.addEventListener('loadedmetadata', onMetadata, { once: true });
        video.addEventListener('error', onError, { once: true });
        removeMetadataListener = () => video.removeEventListener('loadedmetadata', onMetadata);
        removeErrorListener = () => video.removeEventListener('error', onError);
        metadataTimer = window.setTimeout(() => {
          if (!cancelled && video.readyState < 1) this.enableFallback(video);
        }, 3000);
        video.load();
      }

      return () => {
        cancelled = true;
        window.clearTimeout(metadataTimer);
        removeMetadataListener();
        removeErrorListener();
        this.scrollTween?.kill();
        this.scrollTween = null;
        video.pause();
        this.cancelPendingPanelReveal();
        this.pendingVideoSeek = null;
        window.cancelAnimationFrame(this.videoSeekFrame);
        this.videoSeekFrame = 0;
        this.classList.remove('is-scroll-ready');
        window.gsap.set([this.media, this.anchorLayer, ...this.panels], { clearProps: 'all' });
        this.showStaticState();
      };
    }

    renderProgress(progress, video, seekEnd, startX = -78, restingX = 0) {
      const clamped = Math.min(Math.max(progress, 0), 1);
      const videoProgress = Math.min(clamped / this.videoPhaseEnd, 1);
      const nextTime = seekEnd * videoProgress;

      this.scheduleVideoSeek(video, nextTime);
      const mediaSaturate = 88 + 12 * videoProgress;
      const mediaBrightness = 96 + 4 * videoProgress;
      window.gsap.set(this.media, {
        xPercent: restingX + startX * (1 - videoProgress),
        filter: `saturate(${mediaSaturate}%) brightness(${mediaBrightness}%)`,
      });

      const shouldShowAnchors = clamped >= this.videoPhaseEnd - 0.002;
      this.setAnchorLayerVisibility(shouldShowAnchors);

      if (!shouldShowAnchors) {
        this.activeIndex = -1;
        this.updateAnchorAccessibility(-1);
        return;
      }

      const anchorProgress = (clamped - this.videoPhaseEnd) / (1 - this.videoPhaseEnd);
      const playbackOrder = this.anchorPlaybackOrder.length ? this.anchorPlaybackOrder : this.getAnchorPlaybackOrder();
      const orderStep = Math.min(Math.floor(anchorProgress * playbackOrder.length), playbackOrder.length - 1);
      const nextIndex = playbackOrder[orderStep] ?? 0;
      this.setActiveAnchor(nextIndex);
    }

    setAnchorLayerVisibility(visible) {
      if (this.isAnchorLayerVisible === visible) return;

      this.isAnchorLayerVisible = visible;
      this.classList.toggle('is-anchor-content-visible', visible);
      this.anchorLayer.classList.toggle('is-visible', visible);
      window.gsap.to(this.anchorLayer, {
        autoAlpha: visible ? 1 : 0,
        duration: 0.35,
        overwrite: 'auto',
      });

      if (!visible) {
        this.cancelPendingPanelReveal();
        window.gsap.to(this.panels, { autoAlpha: 0, x: 0, duration: 0.25, overwrite: true });
      }
    }

    setActiveAnchor(index, immediate = false, reopenClosed = false) {
      if (index < 0 || index >= this.panels.length) return;

      const previousPanel = this.panels[this.activeIndex];
      const nextPanel = this.panels[index];

      if (nextPanel.classList.contains('is-closed') && !reopenClosed) {
        nextPanel.hidden = true;
        nextPanel.setAttribute('aria-hidden', 'true');
        this.activeIndex = -1;
        this.updateAnchorAccessibility(-1);
        return;
      }

      if (reopenClosed) {
        nextPanel.style.removeProperty('display');
        nextPanel.style.removeProperty('opacity');
        nextPanel.style.removeProperty('visibility');
      }

      if (index === this.activeIndex) {
        nextPanel.hidden = false;
        if (reopenClosed) nextPanel.classList.remove('is-closed');
        nextPanel.classList.add('is-active');
        nextPanel.setAttribute('aria-hidden', 'false');
        this.updateAnchorAccessibility(index);
        return;
      }

      this.cancelPendingPanelReveal();

      if (previousPanel) {
        previousPanel.classList.remove('is-active');
        previousPanel.hidden = true;
      }

      nextPanel.hidden = false;
      if (reopenClosed) nextPanel.classList.remove('is-closed');
      nextPanel.classList.add('is-active');
      nextPanel.classList.add('is-pending');
      nextPanel.setAttribute('aria-hidden', 'false');
      this.activeIndex = index;
      this.updateAnchorAccessibility(index);

      if (immediate || !window.gsap) {
        nextPanel.classList.remove('is-pending');
        nextPanel.style.opacity = '1';
        nextPanel.style.visibility = 'visible';
        nextPanel.style.transform = 'translate(0, 0)';
        if (window.gsap) window.gsap.set(nextPanel, { autoAlpha: 1, x: 0 });
        return;
      }

      const revealToken = this.panelRevealToken;
      const revealPanel = () => {
        if (
          revealToken !== this.panelRevealToken
          || this.activeIndex !== index
          || nextPanel.hidden
        ) return;

        const panelRect = nextPanel.getBoundingClientRect();
        if (panelRect.width < 1 || panelRect.height < 1) {
          this.panelRevealFrame = window.requestAnimationFrame(revealPanel);
          return;
        }

        this.panelRevealFrame = 0;
        nextPanel.classList.remove('is-pending');
        window.gsap.fromTo(
          nextPanel,
          { autoAlpha: 0, x: 0 },
          { autoAlpha: 1, x: 0, duration: 0.48, ease: 'power2.out', overwrite: true },
        );
      };

      this.panelRevealFrame = window.requestAnimationFrame(() => {
        this.panelRevealFrame = window.requestAnimationFrame(revealPanel);
      });
    }

    cancelPendingPanelReveal() {
      this.panelRevealToken += 1;
      window.cancelAnimationFrame(this.panelRevealFrame);
      this.panelRevealFrame = 0;
      this.panels?.forEach((panel) => panel.classList.remove('is-pending'));
    }

    updateAnchorAccessibility(activeIndex) {
      this.anchors.forEach((anchor, index) => {
        const isActive = index === activeIndex;
        anchor.classList.toggle('is-active', isActive);
        anchor.setAttribute('aria-selected', String(isActive));
        anchor.tabIndex = isActive || activeIndex < 0 ? 0 : -1;
      });
    }

    getAnchorSnapPoints() {
      const count = Math.max(this.panels.length, 1);
      return Array.from({ length: count }, (_, index) =>
        this.videoPhaseEnd + ((index + 0.5) / count) * (1 - this.videoPhaseEnd),
      ).concat(1);
    }

    getAnchorPlaybackOrder() {
      const ordered = this.anchors
        .map((anchor, index) => ({
          index,
          x: this.getAnchorDesktopX(anchor),
        }))
        .sort((a, b) => b.x - a.x)
        .map((item) => item.index);

      return ordered.length ? ordered : this.panels.map((_, index) => index).reverse();
    }

    getAnchorDesktopX(anchor) {
      const inlineX = Number.parseFloat(anchor.style.getPropertyValue('--l20-four-anchor-x'));
      if (Number.isFinite(inlineX)) return inlineX;

      const computedX = Number.parseFloat(window.getComputedStyle(anchor).getPropertyValue('--l20-four-anchor-x'));
      return Number.isFinite(computedX) ? computedX : 0;
    }

    enableMobileCarousel(activeVideo) {
      this.cancelPendingPanelReveal();
      this.isMobileAnimating = false;
      this.classList.remove('is-scroll-ready');
      this.classList.add('is-anchor-content-visible');
      this.classList.add('is-mobile-carousel');

      if (this.anchorLayer) {
        this.anchorLayer.classList.remove('is-visible');
        this.anchorLayer.style.opacity = '';
        this.anchorLayer.style.visibility = '';
      }

      this.panels.forEach((panel, index) => {
        panel.hidden = false;
        panel.classList.remove('is-closed');
        panel.classList.add('is-active');
        panel.style.opacity = '1';
        panel.style.visibility = 'visible';
        panel.style.display = '';
        panel.style.transform = '';
        panel.setAttribute('aria-hidden', 'false');
        panel.tabIndex = index === 0 ? 0 : -1;
      });

      this.activeIndex = 0;
      this.updateAnchorAccessibility(0);
      if (this.panelsContainer) this.panelsContainer.scrollLeft = 0;
      this.panelsContainer?.addEventListener('scroll', this.boundMobileScroll, { passive: true });
      this.updateMobileNavigation(0);

      if (activeVideo) {
        activeVideo.controls = false;
        activeVideo.muted = true;
        activeVideo.defaultMuted = true;
        activeVideo.loop = true;
        activeVideo.playsInline = true;
        activeVideo.setAttribute('muted', '');
        activeVideo.setAttribute('playsinline', '');
        this.primeVideo(activeVideo, false);
      }
    }

    disableMobileCarousel(activeVideo) {
      this.cancelPendingPanelReveal();
      this.classList.remove('is-mobile-carousel');
      this.panelsContainer?.removeEventListener('scroll', this.boundMobileScroll);
      window.cancelAnimationFrame(this.mobileScrollFrame);
      window.cancelAnimationFrame(this.mobileAnimationFrame);
      this.mobileCarouselTween?.kill();
      this.mobileCarouselTween = null;
      this.isMobileAnimating = false;
      if (this.panelsContainer) {
        this.panelsContainer.style.scrollSnapType = '';
        this.panelsContainer.style.willChange = '';
      }
      if (activeVideo) activeVideo.pause();
      const lastIndex = Math.max(this.panels.length - 1, 0);
      this.panels.forEach((panel) => {
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('tabindex');
        panel.hidden = true;
        panel.classList.remove('is-closed');
        panel.classList.remove('is-active');
        panel.style.opacity = '';
        panel.style.visibility = '';
        panel.style.display = '';
        panel.style.transform = '';
      });
      if (this.panels[lastIndex]) {
        this.panels[lastIndex].hidden = false;
        this.panels[lastIndex].classList.remove('is-closed');
        this.panels[lastIndex].classList.add('is-active');
      }
      this.activeIndex = lastIndex;
      this.updateAnchorAccessibility(lastIndex);
    }

    moveMobileCarousel(direction) {
      if (!window.matchMedia('(max-width: 768.98px)').matches || this.panels.length <= 1) return;

      const nextIndex = (this.activeIndex + direction + this.panels.length) % this.panels.length;
      const panel = this.panels[nextIndex];
      if (!panel || nextIndex === this.activeIndex) return;

      const left = this.getMobilePanelScrollLeft(panel);
      this.updateMobileNavigation(nextIndex);
      this.animateMobileCarousel(left, nextIndex);
    }

    getMobilePanelScrollLeft(panel) {
      if (!this.panelsContainer || !panel) return 0;

      return panel.offsetLeft - (this.panelsContainer.clientWidth - panel.offsetWidth) / 2;
    }

    animateMobileCarousel(targetLeft, targetIndex) {
      window.cancelAnimationFrame(this.mobileAnimationFrame);
      this.mobileCarouselTween?.kill();
      this.mobileCarouselTween = null;

      const complete = () => {
        this.panelsContainer.scrollLeft = targetLeft;
        this.panelsContainer.style.scrollSnapType = '';
        this.panelsContainer.style.willChange = '';
        this.isMobileAnimating = false;
        this.updateMobileNavigation(targetIndex);
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        complete();
        return;
      }

      const startLeft = this.panelsContainer.scrollLeft;
      const distance = targetLeft - startLeft;
      this.isMobileAnimating = true;
      this.panelsContainer.style.scrollSnapType = 'none';
      this.panelsContainer.style.willChange = 'scroll-position';

      if (window.gsap) {
        this.mobileCarouselTween = window.gsap.to(this.panelsContainer, {
          scrollLeft: targetLeft,
          duration: 0.45,
          ease: 'power2.inOut',
          overwrite: true,
          onComplete: complete,
        });
        return;
      }

      const startTime = performance.now();
      const duration = 450;

      const animate = (time) => {
        const progress = Math.min((time - startTime) / duration, 1);
        const eased = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        this.panelsContainer.scrollLeft = startLeft + distance * eased;

        if (progress < 1) {
          this.mobileAnimationFrame = window.requestAnimationFrame(animate);
          return;
        }

        complete();
      };

      this.mobileAnimationFrame = window.requestAnimationFrame(animate);
    }

    handleMobileScroll() {
      if (this.isMobileAnimating) return;
      window.cancelAnimationFrame(this.mobileScrollFrame);
      this.mobileScrollFrame = window.requestAnimationFrame(() => {
        const containerRect = this.panelsContainer.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;
        const nearestIndex = this.panels.reduce((nearest, panel, index) => {
          if (panel.classList.contains('is-closed')) return nearest;
          const panelRect = panel.getBoundingClientRect();
          const distance = Math.abs(panelRect.left + panelRect.width / 2 - containerCenter);
          return distance < nearest.distance ? { index, distance } : nearest;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
        this.updateMobileNavigation(nearestIndex);
      });
    }

    updateMobileNavigation(index) {
      this.activeIndex = index;
      this.panels.forEach((panel, panelIndex) => {
        panel.tabIndex = panelIndex === index ? 0 : -1;
      });
      const disableNavigation = this.panels.length <= 1;
      if (this.mobilePrevButton) this.mobilePrevButton.disabled = disableNavigation;
      if (this.mobileNextButton) this.mobileNextButton.disabled = disableNavigation;
    }

    handleAnchorClick(event) {
      event.preventDefault();
      const index = Number.parseInt(event.currentTarget.dataset.index, 10);

      if (Number.isFinite(index)) this.setActiveAnchor(index, !window.gsap, true);
    }

    handleAnchorKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const currentIndex = Number.parseInt(event.currentTarget.dataset.index, 10);
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (currentIndex + direction + this.anchors.length) % this.anchors.length;
      this.anchors[nextIndex]?.focus();
      this.setActiveAnchor(nextIndex, !window.gsap, true);
    }

    seekVideo(video, time) {
      try {
        video.currentTime = time;
      } catch (error) {
        // Safari can reject a seek briefly while the media source is attaching.
      }
    }

    scheduleVideoSeek(video, time) {
      if (!video || !Number.isFinite(time)) return;

      this.pendingVideoSeek = { video, time };
      if (this.videoSeekFrame) return;

      this.videoSeekFrame = window.requestAnimationFrame(() => {
        this.videoSeekFrame = 0;
        const pendingSeek = this.pendingVideoSeek;
        this.pendingVideoSeek = null;

        if (!pendingSeek || pendingSeek.video !== video || video.readyState < 1) return;
        if (Math.abs(video.currentTime - pendingSeek.time) <= 0.025) return;
        this.seekVideo(video, pendingSeek.time);
      });
    }

    enableFallback(activeVideo = this.getActiveVideo(window.innerWidth >= 769)) {
      this.classList.remove('is-scroll-ready');
      if (activeVideo) activeVideo.controls = true;
      if (activeVideo) this.primeVideo(activeVideo, false);
      this.showStaticState();
    }

    primeVideo(video, pauseAfterPrime = true) {
      if (!video) return;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            if (pauseAfterPrime) video.pause();
          })
          .catch(() => {});
      } else if (pauseAfterPrime) {
        video.pause();
      }
    }

    showStaticState() {
      this.cancelPendingPanelReveal();
      this.classList.add('is-anchor-content-visible');
      this.anchorLayer?.classList.add('is-visible');
      if (this.anchorLayer) {
        this.anchorLayer.style.opacity = '1';
        this.anchorLayer.style.visibility = 'visible';
      }
      const lastIndex = Math.max(this.panels.length - 1, 0);
      this.panels.forEach((panel, index) => {
        const isActive = index === lastIndex;
        panel.hidden = !isActive;
        panel.classList.remove('is-closed');
        panel.classList.toggle('is-active', isActive);
      });
      this.activeIndex = lastIndex;
      this.updateAnchorAccessibility(lastIndex);
    }
  }

  customElements.define('l20-four-bikes', L20FourBikes);
}
